import { useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { EventCalendarPremium } from '@mui/x-scheduler-premium'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ClockIcon, CancelIcon } from 'src/v3/core/icons'
import type { EventV2 } from '../types'
import type { HydratedSessionV3 } from '../events-v2.slice'
import {
  deriveRegistrationState,
  deriveRsvpState,
  type RsvpState,
} from '../utils/rsvp-state'
import {
  rsvpSchedulerColor,
  getRsvpColors,
  getRsvpLegendItems,
  rsvpLabelShort,
  SCHEDULER_SESSION_PREFIX,
  type SchedulerColor,
} from '../utils/constants'

dayjs.extend(utc)
dayjs.extend(timezone)

let LOCAL_TZ = dayjs.tz.guess()

let formatSessionTime = (session: HydratedSessionV3): string => {
  let start = dayjs.utc(session.sessionTime).tz(LOCAL_TZ)
  let tzAbbr = start.format('z')
  if (session.endTime) {
    let end = dayjs.utc(session.endTime).tz(LOCAL_TZ)
    return `${start.format('ddd, MMM D · h:mm A')} – ${end.format('h:mm A')} ${tzAbbr}`
  }
  return `${start.format('ddd, MMM D · h:mm A')} ${tzAbbr}`
}

type CalendarEventItem = {
  id: string
  title: string
  start: string
  end: string
  color: SchedulerColor
  className: string
  rsvpState: RsvpState
}

type Props = {
  sessions: HydratedSessionV3[]
  events: EventV2[]
}

export default function EventsCalendar({ sessions, events }: Props) {
  let navigate = useNavigate()
  let theme = useTheme()
  let [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  let [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)

  let eventsById = useMemo(
    () => new Map(events.map((e) => [e.id, e])),
    [events],
  )

  let sessionById = useMemo(
    () => new Map(sessions.map((s) => [s.id, s])),
    [sessions],
  )

  let calendarEvents = useMemo(
    (): CalendarEventItem[] =>
      sessions.flatMap((session) => {
        let event = eventsById.get(session.eventID)
        if (!event) return []
        let rsvpState: RsvpState =
          deriveRsvpState(session.invites) ??
          deriveRegistrationState(
            session.sessionTime,
            session.endTime,
            session.registrationDeadline,
          )
        let color = rsvpSchedulerColor[rsvpState]
        if (!color) return []
        return [
          {
            id: session.id,
            title: session.name
              ? `${event.name} – ${session.name}`
              : event.name,
            start: session.sessionTime,
            end:
              session.endTime ??
              dayjs.utc(session.sessionTime).add(2, 'hour').toISOString(),
            color,
            className: `${SCHEDULER_SESSION_PREFIX}${session.id}`,
            rsvpState,
          },
        ]
      }),
    [sessions, eventsById],
  )

  let calendarEventsById = useMemo(
    () => new Map(calendarEvents.map((e) => [e.id, e])),
    [calendarEvents],
  )

  // Intercept event clicks/keyboard activation in capture phase to suppress
  // the built-in edit dialog and open our custom popover instead.
  let openSessionPopover = (
    e: { stopPropagation: () => void },
    target: EventTarget | null,
  ) => {
    if (!(target instanceof Element)) return
    let closest = target.closest(`[class*="${SCHEDULER_SESSION_PREFIX}"]`)
    if (!closest) return
    let cls = Array.from(closest.classList).find((c) =>
      c.startsWith(SCHEDULER_SESSION_PREFIX),
    )
    if (!cls) return
    e.stopPropagation()
    if (!(closest instanceof HTMLElement)) return
    let sessionId = cls.slice(SCHEDULER_SESSION_PREFIX.length)
    setSelectedSessionId(sessionId)
    setPopoverAnchor(closest)
  }

  let handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) =>
    openSessionPopover(e, e.target)

  let handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    let { target } = e
    if (!(target instanceof Element)) return
    if (!target.closest(`[class*="${SCHEDULER_SESSION_PREFIX}"]`)) return
    if (e.key === ' ') e.preventDefault()
    openSessionPopover(e, target)
  }

  let handlePopoverClose = () => {
    setPopoverAnchor(null)
  }

  let selectedSession = selectedSessionId
    ? sessionById.get(selectedSessionId)
    : null
  let selectedEvent = selectedSession
    ? eventsById.get(selectedSession.eventID)
    : null
  let selectedRsvpState = selectedSessionId
    ? (calendarEventsById.get(selectedSessionId)?.rsvpState ?? null)
    : null

  return (
    <Box>
      <Box
        onClickCapture={handleClickCapture}
        onKeyDownCapture={handleKeyDownCapture}
      >
        <EventCalendarPremium
          events={calendarEvents}
          defaultView="month"
          readOnly
          preferencesMenuConfig={false}
        />
      </Box>

      <Popover
        open={Boolean(
          popoverAnchor &&
          selectedSession &&
          selectedEvent &&
          selectedRsvpState,
        )}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': 'Session details',
            sx: { mt: 0.5 },
          },
        }}
      >
        {selectedSession && selectedEvent && selectedRsvpState && (
          <Box
            sx={{ p: 2, minWidth: 260, maxWidth: 340, position: 'relative' }}
          >
            <IconButton
              onClick={handlePopoverClose}
              size="small"
              aria-label="Close"
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                color: 'text.secondary',
              }}
            >
              <CancelIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, pr: 3, lineHeight: 1.3, mb: 0.5 }}
            >
              {selectedEvent.name}
            </Typography>

            {selectedSession.name && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
                {selectedSession.name}
              </Typography>
            )}

            <Box className="flex items-center gap-1.5" sx={{ mb: 1.5 }}>
              <ClockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {formatSessionTime(selectedSession)}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Chip
                label={rsvpLabelShort[selectedRsvpState] ?? ''}
                size="small"
                sx={{
                  bgcolor: getRsvpColors(theme)[selectedRsvpState],
                  color: '#fff',
                  fontSize: 11,
                  height: 22,
                }}
              />
            </Box>

            <Button
              variant="contained"
              size="small"
              onClick={() => {
                handlePopoverClose()
                navigate(`/events/v2/${selectedEvent.id}`)
              }}
            >
              View event details
            </Button>
          </Box>
        )}
      </Popover>

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, mt: 2, px: 1 }}>
        {getRsvpLegendItems(theme).map((item) => (
          <Box key={item.label} className="flex items-center gap-1.5">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: item.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
